/**
 * §4.4: ploegen and ondersteuners on the banen, with the waarschuwingen the engine gives.
 * Only the frame is here: the server owns the waarschuwing sentences, keyed on a stable code.
 */
export const planning = {
  lijst: {
    label: "Wedstrijddagen",
    datum: "Datum",
    niveau: "Niveau",
    korps: "Organiserend korps",
    nogNietBekend: "Nog niet bekend",
    waarschuwingen: "Waarschuwingen",
    geen: "-",
    zoek: "Zoek op datum of korps",
    filterWaarschuwingen: "Alleen dagen met waarschuwingen",
    leeg: "Dit seizoen heeft nog geen wedstrijddagen.",
    terug: "Alle wedstrijddagen",
    kies: "Kies een wedstrijddag om het speelschema te zien.",
  },

  fout: {
    title: "Kon de planning niet ophalen",
  },

  nogGeen: {
    title: "Nog geen planning",
    description:
      "De planner heeft dit seizoen nog niet gedraaid. De planner start vanzelf zodra er iets verandert dat de planning raakt, zoals een korps dat een ploeg inschrijft of een wedstrijddag die verschuift.",
  },

  stand: {
    definitief: "Definitief",
    concept: "Concept",
    laatstBerekend: "Laatst berekend op {{moment}}",
    nooitBerekend: "Nog niet berekend",
    draait: "De planner draait…",
    vernieuwen: "Vernieuwen",
    herbereken: "Herberekenen",
    /** §4.4.6 stops the engine once every niveau is definitief, so the button says why. */
    definitiefTitel:
      "Alle niveaus zijn definitief. De planner draait er niet meer op.",
    definitiefMaken: "Definitief maken",
    definitiefMakenVoor: "Planning van {{niveau}} definitief maken",
    terugzetten: "Terugzetten naar concept",
    terugzettenVoor: "Planning van {{niveau}} terugzetten naar concept",
    bevestig: {
      titel: "Planning van {{niveau}} definitief maken?",
      /* Says what follows, not that it is irreversible: the button that undoes it replaces this. */
      uitleg:
        "De planning van {{niveau}} wordt zichtbaar voor korpsen en ondersteuners. De planner draait er niet meer op, en wijzigen kan daarna alleen nog met de hand. De andere niveaus veranderen niet. Je kunt dit niveau later terugzetten naar concept.",
      bevestigen: "Definitief maken",
    },
  },

  niveaus: {
    label: "Status per niveau",
    definitiefOp: "Definitief sinds {{moment}}",
    definitiefOpDoor: "Definitief sinds {{moment}} door {{naam}}",
  },

  ongeplaatst: {
    title_one: "{{count}} ploeg zonder plaats",
    title_other: "{{count}} ploegen zonder plaats",
    description:
      "De planner kon deze ploegen niet plaatsen. Zet ze zelf vast op een vrije starttijd, of pas aan wat ze tegenhoudt.",
    regel: "{{korps}} · {{klasse}}",
    /** The free starttijden the wedstrijdregels rule out. The server owns the reden itself. */
    geblokkeerdTitel:
      "Deze starttijden zijn vrij, maar de wedstrijdregels sluiten ze uit:",
    geblokkeerd: "{{datum}} · {{baan}} · {{tijd}}. {{reden}}",
    /** One heading for every reden: the reden already says what is wrong with them. */
    plekkenTitel: "Het gaat om:",
    /** The korps beside the datum: a seizoen runs two dagen on one date. */
    plek: "{{datum}} · {{korps}}",
    /** And the baan too, where the reden got as far as one. */
    plekMetBaan: "{{datum}} · {{korps}} · {{baan}}",
  },

  grid: {
    verplaatsen:
      "<strong>{{naam}}</strong> verplaatsen. Kies een starttijd, op deze of een andere wedstrijddag. Staat er al een ploeg? Dan kies je wat er met die ploeg gebeurt.",
    annuleren: "Annuleren",
    dagRegel: "{{niveau}} · {{korps}}",
    geenKorps:
      "Deze wedstrijddag heeft nog geen organiserend korps, dus de planner slaat die over. Wijs er een aan bij Wedstrijddagen.",
    geenBanen: "Deze wedstrijddag heeft nog geen banen.",
    /* Keyed on "no ploeg stands on this dag", not on the niveau the model cannot speak to
       (ADR-0029): a finale waits for the promoties a beheerder enters by hand (ADR-0058). */
    geenPloegen:
      "Er staat nog geen ploeg op deze wedstrijddag. Bij een halve finale of finale is dat normaal. De ploegen komen pas als je de promoties invult op de planning van de wedstrijddag ervoor. Zie Promoties in de handleiding.",
    caption: "Speelschema van {{datum}}: starttijden per wedstrijdbaan",
    start: "Start",
    leeg: "-",
    vrij: "Vrij",
    vastgezet: "Vastgezet",
    vastgezetMetReden: "Vastgezet: {{reden}}",
  },

  bezetting: {
    title: "Bezetting",
    description:
      "De functies die deze wedstrijddag en de banen erop nodig hebben.",
    heleDag: "Hele dag",
    perBaan: "Per wedstrijdbaan",
    caption: "{{kop}}: wie welke functie vervult op {{datum}}",
    functie: "Functie",
    nietBezet: "Niet bezet",
    nietNodig: "niet nodig",
    doelLabel: "{{datum}} · {{kolom}}",
  },

  waarschuwingen: {
    title: "Waarschuwingen",
    description:
      "Deze punten kloppen niet met de wedstrijdregels. Wat met de hand is vastgezet, gaat voor de wedstrijdregels. Daarom blijft de planning zo staan en zie je de punten hier.",
    /** Where the waarschuwing is: the server's sentence names no baan, and a dag runs several. */
    regel: "{{baan}} · {{tekst}}",
    /** For a waarschuwing about the dag rather than one baan - the rekenkamer (ADR-0021). */
    heleDag: "Hele dag",
  },

  /**
   * Deliberately not a waarschuwing: a samenvoorkeur loses to a voorkeursdatum by design, so
   * this is the planner working (ADR-0052). No remedy line, because there is nothing to fix.
   * Grouped by category rather than one line per korps (#152): the server's sentences already
   * do the counting, so the title only adds how many korpsen that spans in total.
   */
  verspreid: {
    title_one: "{{count}} korps niet samen ingedeeld",
    title_other: "{{count}} korpsen niet samen ingedeeld",
    description:
      "Deze korpsen vroegen om hun ploegen op één wedstrijddag. Dat is niet gelukt: een voorkeursdatum of een volle baan ging voor.",
  },

  keuzelijst: {
    zoekOndersteuners_one: "Zoek in {{count}} ondersteuner",
    zoekOndersteuners_other: "Zoek in {{count}} ondersteuners",
    zoekPloegen_one: "Zoek in {{count}} ploeg",
    zoekPloegen_other: "Zoek in {{count}} ploegen",
    zoekLabel: "Zoek {{naam}}",
    nietsGevonden: "Niets gevonden.",
    ondersteuner: "ondersteuner",
    ploeg: "ploeg",
  },

  /** One dialog for one vakje: vrij of bezet, indelen of verplaatsen. */
  cel: {
    verplaatsTitle: "{{naam}} verplaatsen",
    indelenTitle: "Ploeg indelen",
    beschrijving: "{{baan}} om {{tijd}}",
    vanKorps: " · {{korps}}",
    vastgezetSuffix: " · vastgezet",
    verkeerdeKlasse:
      "{{naam}} speelt een andere klasse dan {{klasse}}. Kies een baan van de eigen klasse.",
    ploeg: "Ploeg",
    ploegUitleg:
      "Ploegen die nog nergens staan, staan bovenaan. Kies je een ploeg die al ergens anders ingedeeld is? Dan verhuist die ploeg hierheen.",
    nogNietIngedeeld: " · nog niet ingedeeld",
    leeg: "Er is nog geen ploeg ingeschreven voor {{klasse}}.",

    /* Never picked for the beheerder: one choice leaves both ploegen playing, the other takes
       one out of the schema. */
    bezet: {
      vraag: "Wat gebeurt er met {{naam}}?",
      ruilen: "Plekken ruilen",
      overschrijven: "Uit het schema halen",
      ruilenUitleg:
        "{{naam}} krijgt de plek van {{gekozen}}. Beide ploegen blijven ingedeeld. Daarna staan ze allebei vast. De reden die je hieronder invult, komt op de plek van {{gekozen}} te staan.",
      /* The other vakje falls open too: this arm only appears where {{gekozen}} has a plek. */
      overschrijvenUitleg:
        "{{naam}} raakt deze plek kwijt en komt bij de ploegen zonder plaats. Het vakje waar {{gekozen}} nu staat, komt vrij. Zolang het niveau concept is, kan de planner {{naam}} zelf ergens anders neerzetten.",
      alleenOverschrijven:
        "{{gekozen}} staat zelf nog nergens op dit niveau, dus ruilen kan niet. {{naam}} raakt deze plek kwijt en komt bij de ploegen zonder plaats.",
      /** A pin the beheerder is about to throw away is worth one sentence in front of it. */
      vastzettingVervalt:
        "{{naam}} staat hier vastgezet. Die vastzetting vervalt.",
    },

    reden: "Reden",
    redenPlaceholder: "Waarom staat deze ploeg hier vast?",
    redenUitleg:
      "Optioneel, maar de reden blijft bij dit vakje staan. Vastzetten houdt deze ploeg hier tot iemand de ploeg weer losmaakt, ook als de planner daarna opnieuw draait.",
    /* Two buttons that both empty something, so both say what they leave behind. */
    losmakenUitleg:
      "Losmaken laat {{naam}} staan en geeft dit vakje terug aan de planner. Uit het schema halen zet {{naam}} bij de ploegen zonder plaats. Zolang het niveau concept is, kan de planner de ploeg daarna zelf ergens neerzetten.",
    losmaken: "Losmaken",
    /* The same words as the ruil dialog's other arm, because it is the same act: the ploeg
       leaves the schema and joins the ploegen zonder plaats. */
    verwijderen: "Uit het schema halen",
    verplaatsen: "Verplaatsen",
    annuleren: "Annuleren",
    vastzetten: "Vastzetten",
  },

  bezettingDialog: {
    beschrijvingBezet: "{{doel}} · nu {{persoon}}",
    beschrijvingVrij: "{{doel}} · nog niet bezet",
    ondersteuner: "Ondersteuner",
    ondersteunerUitleg:
      "Bovenaan staat wie op deze dag kan en bevoegd is voor {{functie}}. Je mag ook iemand anders kiezen. Omdat je die persoon zelf vastzet, blijft de waarschuwing daarover uit.",
    ophalen: "Bezig met ophalen…",
    nietAangemeld: " · kan niet of is niet bevoegd",
    alIngezet: " · al ingezet op deze dag",
    leeg: "Er zijn nog geen personen bekend.",
    reden: "Reden",
    redenPlaceholder: "Waarom staat deze persoon hier vast?",
    redenUitleg:
      "Optioneel, maar de reden blijft bij de functie staan. Vastzetten houdt deze functie bezet tot iemand de functie weer losmaakt.",
    losmaken: "Losmaken",
    annuleren: "Annuleren",
    vastzetten: "Vastzetten",
  },

  promotie: {
    title: "Promoties",
    explanation:
      "Kies per promotieplaats de ploeg die daar is geëindigd. De Wedstrijdplanner schrijft de ploeg dan namens het korps in voor de wedstrijddag die erbij staat. Kies daarna Herberekenen op de planning van die dag.",
    plaats: "Plaats {{positie}} gaat naar {{bestemming}}",
    pick: "Ploeg op plaats {{positie}}",
    nonePicked: "Kies een ploeg",
    undo: "Ongedaan maken",
    undoFor: "Promotie van {{ploeg}} ongedaan maken",
  },

  errors: {
    promotieMark: "Kon de promotie niet vastleggen.",
    promotieUndo: "Kon de promotie niet ongedaan maken.",
    ophalen: "Kon de planning niet ophalen.",
    kandidaten: "Kon de kandidaten niet ophalen.",
    herberekening: "Kon de herberekening niet aanvragen.",
    definitiefMaken: "Kon het niveau niet definitief maken.",
    terugzetten: "Kon het niveau niet terugzetten naar concept.",
    ploegVastzetten: "Kon de ploeg niet vastzetten.",
    ploegLosmaken: "Kon de ploeg niet losmaken.",
    ploegVerwijderen: "Kon de ploeg niet uit dit vakje halen.",
    functieVastzetten: "Kon de functie niet vastzetten.",
    functieLosmaken: "Kon de functie niet losmaken.",
  },
} as const;
